/**
 * search-drawer.js — mobile inline search bar open/close.
 *
 * The bar lives inside the <header> as `[data-search-drawer]` and
 * expands inline below the top row when the user taps a search toggle.
 * No focus trap or scroll lock — this isn't a modal, it's just a
 * collapsible region that scrolls naturally with the page.
 *
 * `predictive-search.js` owns the dropdown inside it; this file only
 * handles open/close + focus management.
 *
 * Wires:
 *   • [data-search-toggle] — opens (or toggles if already open)
 *   • [data-search-close]  — closes
 *   • Escape               — closes when focus is inside the bar
 */
(() => {
  const drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return;

  const input = drawer.querySelector('[data-predictive-input]');
  const toggles = document.querySelectorAll('[data-search-toggle]');

  const isOpen = () => drawer.getAttribute('aria-hidden') === 'false';

  const open = () => {
    if (isOpen()) return;
    drawer.setAttribute('aria-hidden', 'false');
    toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    // Focus the input on the next frame so the CSS slide-down can begin
    // without the keyboard appearing mid-animation on iOS.
    requestAnimationFrame(() => {
      input?.focus({ preventScroll: false });
    });
  };

  const close = ({ keepInput = false } = {}) => {
    if (!isOpen()) return;
    drawer.setAttribute('aria-hidden', 'true');
    toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    // Clear the input on close so a fresh open doesn't show stale
    // results. Skip when caller wants to preserve (e.g. on submit).
    if (input && !keepInput) {
      input.value = '';
      // predictive-search.js listens on `input` events to refresh /
      // clear its dropdown — dispatching one lets it react.
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  toggles.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (isOpen()) close();
      else open();
    });
  });

  drawer.querySelectorAll('[data-search-close]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      close();
    });
  });

  // Escape closes only when focus is within the bar — outside the bar,
  // the key should bubble to whatever component owns the focus (e.g.
  // an open modal elsewhere on the page).
  drawer.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) {
      e.preventDefault();
      close();
      // Move focus back to the toggle so keyboard users land predictably.
      toggles[0]?.focus();
    }
  });
})();
